import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';

function PandaMark() {
  return (
    <Box sx={{ position: 'relative', width: { xs: 58, md: 74, lg: 86 }, height: { xs: 54, md: 70, lg: 80 }, flex: '0 0 auto' }}>
      <Box sx={{ position: 'absolute', left: 6, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', right: 8, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', left: 6, top: 8, width: { xs: 46, md: 56, lg: 66 }, height: { xs: 46, md: 56, lg: 66 }, borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 4px 0 rgba(15,23,42,0.18)' }}>
        <Box sx={{ position: 'absolute', left: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(-18deg)' }} />
        <Box sx={{ position: 'absolute', right: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(18deg)' }} />
        <Box sx={{ position: 'absolute', left: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', right: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', left: '44%', top: '56%', width: '12%', height: '9%', borderRadius: '50%', bgcolor: '#111827' }} />
        <Box sx={{ position: 'absolute', left: '39%', top: '66%', width: '22%', height: 8, borderBottom: '2px solid #111827', borderRadius: '0 0 99px 99px' }} />
      </Box>
      <Box sx={{ position: 'absolute', right: { xs: -5, md: 1, lg: -7 }, top: { xs: 18, md: 22, lg: 25 }, width: 18, height: 9, borderRadius: 99, bgcolor: '#fff', transform: 'rotate(38deg)', boxShadow: '0 0 0 3px #111827' }} />
    </Box>
  );
}

function BrandWord() {
  const letters = [
    ['D', '#ef5b8a'],
    ['A', '#f59e0b'],
    ['N', '#84cc16'],
    ['D', '#22c55e'],
    ['U', '#38bdf8'],
  ];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.05, md: 0.2 } }}>
      {letters.map(([letter, color]) => (
        <Box
          key={letter + color}
          component="span"
          sx={{ color, WebkitTextStroke: { xs: '1px #fff', md: '2px #fff' }, textShadow: '0 3px 0 rgba(15,23,42,0.16)', fontWeight: 1000 }}
        >
          {letter}
        </Box>
      ))}
    </Box>
  );
}

function HeaderCloud() {
  return (
    <Box
      sx={{
        display: { xs: 'none', lg: 'block' },
        position: 'absolute',
        left: '71%',
        top: 31,
        width: 64,
        height: 24,
        borderRadius: 99,
        bgcolor: '#fff',
        filter: 'drop-shadow(0 4px 0 rgba(38,20,100,0.18))',
        '&::before, &::after': { content: '""', position: 'absolute', bgcolor: 'inherit', borderRadius: '50%' },
        '&::before': { width: 28, height: 28, left: 10, top: -13 },
        '&::after': { width: 34, height: 34, right: 8, top: -18 },
      }}
    />
  );
}

export default function MiniTaskGameHeader({ classTitle, soundEnabled, onToggleSound, language = 'tr', isEndless = false, soundRequired = false }) {
  const isEnglish = language === 'en';
  return (
    <Box
      className="mini-task-game-header"
      sx={{
        position: 'relative',
        boxSizing: 'border-box',
        mx: { xs: -1.2, sm: -2, md: -3.2 },
        minHeight: { xs: 76, md: 108 },
        borderRadius: '0 0 28px 28px',
        background: 'linear-gradient(180deg, #6d45df 0%, #5128bc 100%)',
        color: '#fff',
        px: { xs: 1.4, sm: 2.4, md: 4 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1, md: 2 },
        boxShadow: '0 6px 0 rgba(39,22,108,0.24), 0 14px 28px rgba(88,28,135,0.18)',
        overflow: 'hidden',
        '@media (max-height: 820px) and (min-width: 600px)': {
          minHeight: 88,
        },
      }}
    >
      <HeaderCloud />
      <StarRoundedIcon sx={{ display: { xs: 'none', lg: 'block' }, position: 'absolute', left: '63%', top: 25, fontSize: 48, color: '#facc15', filter: 'drop-shadow(0 4px 0 rgba(76,29,149,0.34))', transform: 'rotate(-12deg)' }} />
      <Box className="mini-task-game-brand" sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 2 }, minWidth: 0, zIndex: 1 }}>
        <PandaMark />
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: { xs: 1.2, md: 2.4 },
            minWidth: 0,
            flexShrink: 1,
            overflow: 'hidden',
            fontWeight: 1000,
            lineHeight: 1,
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            fontSize: { xs: 28, sm: 42, md: 44, lg: 58 },
            textShadow: '0 3px 0 rgba(15,23,42,0.16)',
          }}
        >
          <BrandWord />
          {isEndless && (
            <Box component="span" sx={{ fontSize: { xs: 14, md: 20 }, color: '#fff', fontWeight: 800, whiteSpace: 'nowrap', textShadow: 'none' }}>
              Sonsuz Karma
            </Box>
          )}
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, color: '#fff', fontSize: { xs: 28, sm: 38, md: 40, lg: 52 } }}>
            {isEnglish ? 'Mini Tasks' : 'Mini Görevler'}
          </Box>
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.8, md: 1.2 }, zIndex: 1 }}>
        <ButtonBase
          aria-label={soundRequired ? 'Bu oyunda ses gerekli' : soundEnabled ? (isEnglish ? 'Mute sound' : 'Sesi kapat') : (isEnglish ? 'Turn on sound' : 'Sesi aç')}
          aria-pressed={soundEnabled}
          disabled={soundRequired}
          onClick={onToggleSound}
          sx={{
            width: { xs: 46, md: 58 },
            height: { xs: 46, md: 58 },
            borderRadius: '50%',
            position: 'relative',
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '2px solid #fff',
            '&.Mui-disabled': {
              bgcolor: 'rgba(21,9,68,0.42)',
              borderStyle: 'dashed',
              cursor: 'not-allowed',
            },
            '&:not(.Mui-disabled):hover': { bgcolor: 'rgba(255,255,255,0.24)' },
            '&:not(.Mui-disabled):active': { bgcolor: 'rgba(255,255,255,0.08)' },
            '&:focus-visible, &.Mui-focusVisible': {
              outline: '3px solid #fff',
              outlineOffset: '3px',
              boxShadow: '0 0 0 3px #3f1b96',
            },
          }}
        >
          {soundEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
          {soundRequired && (
            <LockRoundedIcon
              data-testid="required-audio-lock"
              aria-hidden="true"
              sx={{
                position: 'absolute',
                right: -4,
                bottom: -4,
                width: { xs: 20, md: 22 },
                height: { xs: 20, md: 22 },
                p: '3px',
                boxSizing: 'border-box',
                border: '1px solid #fff',
                borderRadius: '50%',
                bgcolor: '#3f1b96',
                color: '#fff',
                pointerEvents: 'none',
              }}
            />
          )}
        </ButtonBase>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: { xs: 0.7, md: 1 }, bgcolor: 'rgba(39,22,108,0.48)', borderRadius: 99, px: { xs: 1.1, md: 1.5, lg: 2.2 }, py: { xs: 0.9, md: 1.15 }, boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.16)' }}>
          <GroupsRoundedIcon sx={{ fontSize: { xs: 22, md: 30 } }} />
          <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 22, lg: 28 }, lineHeight: 1, whiteSpace: 'nowrap' }}>
            {classTitle || (isEnglish ? 'Class' : 'Sınıf')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
